import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon, IconProps } from '@osmosis/tailwind-ui/components/Icon';
import { H3 } from '@osmosis/tailwind-ui/components/Text';
import React, { CSSProperties, ReactNode } from 'react';

import Divider from '../../Divider';
import LanguageTag from '../../LanguageTag';
import BlurredBlock from './BlurredBlock';
import LearnBlockClient from './LearnBlockClient';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';

export type CommonLearnBlockProps = {
  contentOnly?: boolean;
};

type Props = {
  icon: IconProps['icon'];
  iconStyles?: CSSProperties;
  title: string;
  content?: ReactNode;
  controls?: ReactNode;
  htmlContent?: string | null;
  withBlurredBlock?: boolean;
  blurredBlockType?: string;
  hasDMTAccess?: boolean;
  isLockoutBlock?: boolean;
  denyReason?: string | null;
  resource?: string;
  languageTag?: boolean;
  modalKey?: 'flashcards' | 'questions';
  ImageComponent?: ReactNode;
  classNames?: {
    container?: string;
  };
} & CommonLearnBlockProps;

/**
 * LearnBlock component.
 */
export default function LearnBlock({
  icon,
  iconStyles = {},
  title,
  content = null,
  controls,
  withBlurredBlock,
  hasDMTAccess,
  blurredBlockType,
  isLockoutBlock,
  denyReason,
  resource,
  languageTag,
  modalKey,
  ImageComponent = null,
  contentOnly,
  classNames = {},
}: Props): JSX.Element {
  const showTitle = !contentOnly;
  const hideHeader = !controls && contentOnly;

  return (
    <LearnBlockClient
      isLockoutBlock={isLockoutBlock}
      resource={resource}
      modalKey={modalKey}
      classNames={classNames.container}
    >
      {withBlurredBlock && (
        <BlurredBlock
          blurredBlockType={blurredBlockType}
          hasDMTAccess={hasDMTAccess}
          denyReason={denyReason}
        />
      )}

      <Flex
        data-testid="learn-block-controls"
        className={cn(
          `pb-4 justify-between items-center`,
          hideHeader ? 'pb-0' : '',
          showTitle ? '' : 'justify-end',
        )}
      >
        <Flex
          className={cn(
            `items-center py-1 overflow-hidden`,
            showTitle ? '' : 'hidden',
          )}
        >
          <Box className={`mr-4`}>
            <Icon icon={icon} style={iconStyles} />
          </Box>
          <Flex
            className={`flex-col md:flex-row lg:flex-row  md:items-center gap-1`}
          >
            <H3 className={`mr-4`}>{title}</H3>
            <Box>
              {languageTag && <LanguageTag className="text-xs md:text-sm" />}
            </Box>
          </Flex>
          {ImageComponent}
        </Flex>
        {controls}
      </Flex>
      <Divider className={cn('mb-8', hideHeader ? 'hidden' : '')} />
      <Box data-testid="learn-block-content">{content}</Box>
    </LearnBlockClient>
  );
}
